import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import PolicyConfigTabs, {
	type PolicyConfig,
	type AIProviderName,
	PROVIDER_MODELS,
	DEFAULT_INTEGRATIONS,
	DEFAULT_OAUTH,
	DEFAULT_AI_PROVIDERS,
	DEFAULT_PROVIDER_ORDER,
} from '../components/PolicyConfigTabs';
import SkillManager from '../components/SkillManager';
import { platformLabel, platformIcon } from '../utils/platform';

interface TeamMember {
	user_id: string;
	email: string;
	display_name: string | null;
	role: 'owner' | 'admin' | 'member';
	added_at: number;
	is_current_user: boolean;
}

interface AccessEntry {
	user_id: string;
	email: string;
	display_name: string | null;
	granted_by: string;
	granted_at: number;
}

interface PolicyResponse {
	policy_id: string | null;
	policy_name: string;
	instance_id: string | null;
	config: PolicyConfig;
	version: number;
	is_default: boolean;
	updated_at: number | null;
}

interface BotInstance {
	instance_id: string;
	agent_id: string;
	clawdbot_version: string;
	install_path: string;
	running_status: string;
	agent_hostname?: string;
}

export default function InstanceConfigPage() {
	const { instanceId } = useParams<{ instanceId: string }>();
	const { firebaseUser } = useAuth();
	const { currentTeam } = useTeam();
	const { getHeaders } = useApiHeaders();
	const queryClient = useQueryClient();
	const [instance, setInstance] = useState<BotInstance | null>(null);
	const [policy, setPolicy] = useState<PolicyResponse | null>(null);
	const [teamDefault, setTeamDefault] = useState<PolicyConfig | null>(null);
	const [config, setConfig] = useState<PolicyConfig | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

	const [selectedMemberToAdd, setSelectedMemberToAdd] = useState<string>('');

	// Determine if current user is owner/admin (can manage access).
	// The TeamContext Team interface types role as 'owner' | 'member', but the API
	// actually returns 'owner' | 'admin' | 'member'. Cast to string for runtime check.
	const userRole = currentTeam?.role as string | undefined;
	const isOwnerOrAdmin = userRole === 'owner' || userRole === 'admin';

	// Credential modals
	const [showApiKeyModal, setShowApiKeyModal] = useState<'openai' | 'google_ai' | 'openrouter' | null>(null);
	const [showClaudeTokenModal, setShowClaudeTokenModal] = useState(false);
	const [apiKeyInput, setApiKeyInput] = useState('');
	const [claudeTokenInput, setClaudeTokenInput] = useState('');
	const [savingCredential, setSavingCredential] = useState(false);

	// Channel key assignments for current agent (channel_type → label)
	const [channelKeyAssignments, setChannelKeyAssignments] = useState<Record<string, string>>({});

	// Gateway config editor
	const [gatewayConfig, setGatewayConfig] = useState<string | null>(null);
	const [loadingGatewayConfig, setLoadingGatewayConfig] = useState(false);
	const [savingGatewayConfig, setSavingGatewayConfig] = useState(false);
	const [gatewayConfigStatus, setGatewayConfigStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

	// Init tab state
	const [envVars, setEnvVars] = useState<Array<{ key: string; value: string }>>([]);
	const [initScript, setInitScript] = useState('');
	const [initSaveStatus, setInitSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

	// === Access Tab: Queries & Mutations ===

	// Fetch team members (for the full list and the "Add" dropdown)
	const { data: membersData, isLoading: membersLoading } = useQuery({
		queryKey: ['team', currentTeam?.team_id, 'members'],
		queryFn: async () => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/team/members`, { headers });
			if (!res.ok) throw new Error('Failed to fetch members');
			return res.json();
		},
		enabled: !!firebaseUser,
	});

	// Fetch instance access list
	const { data: accessData, isLoading: accessLoading } = useQuery({
		queryKey: ['instance', instanceId, 'access'],
		queryFn: async () => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access`, { headers });
			if (!res.ok) throw new Error('Failed to fetch access');
			return res.json();
		},
		enabled: !!firebaseUser,
	});

	// Grant access mutation
	const grantMutation = useMutation({
		mutationFn: async (targetUserId: string) => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access`, {
				method: 'POST',
				headers: { ...headers, 'Content-Type': 'application/json' },
				body: JSON.stringify({ user_id: targetUserId }),
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({ error: 'Failed to grant access' }));
				throw new Error(err.error || 'Failed to grant access');
			}
			return res.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['instance', instanceId, 'access'] });
			setSelectedMemberToAdd('');
		},
	});

	// Revoke access mutation
	const revokeMutation = useMutation({
		mutationFn: async (targetUserId: string) => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/access/${targetUserId}`, {
				method: 'DELETE',
				headers,
			});
			if (!res.ok) {
				const err = await res.json().catch(() => ({ error: 'Failed to revoke access' }));
				throw new Error(err.error || 'Failed to revoke access');
			}
			return res.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['instance', instanceId, 'access'] });
		},
	});

	// Fetch team platform identities (admin only)
	const { data: teamIdentitiesData } = useQuery({
		queryKey: ['team', currentTeam?.team_id, 'identities'],
		queryFn: async () => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/identity/team`, { headers });
			if (!res.ok) return { identities: [] };
			return res.json();
		},
		enabled: !!firebaseUser && isOwnerOrAdmin,
	});

	// Fetch init config
	const { data: initData } = useQuery({
		queryKey: ['instance', instanceId, 'init'],
		queryFn: async () => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/init`, {
				headers,
			});
			if (!res.ok) return null;
			return res.json() as Promise<{
				env_vars: Array<{ key: string; value: string }>;
				init_script: string | null;
				last_run_at: number | null;
				last_run_exit_code: number | null;
				last_run_output: string | null;
			}>;
		},
		enabled: !!instanceId && isOwnerOrAdmin,
	});

	useEffect(() => {
		if (initData) {
			setEnvVars(initData.env_vars || []);
			setInitScript(initData.init_script || '');
		}
	}, [initData]);

	// Save init config mutation
	const saveInitMutation = useMutation({
		mutationFn: async () => {
			const headers = await getHeaders();
			const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/init`, {
				method: 'PUT',
				headers: { ...headers, 'Content-Type': 'application/json' },
				body: JSON.stringify({
					env_vars: envVars.filter(v => v.key.trim()),
					init_script: initScript || null,
				}),
			});
			if (!res.ok) throw new Error((await res.json()).error || 'Failed to save');
			return res.json();
		},
		onSuccess: () => {
			setInitSaveStatus({ type: 'success', message: 'Init config saved' });
			queryClient.invalidateQueries({ queryKey: ['instance', instanceId, 'init'] });
			setTimeout(() => setInitSaveStatus(null), 3000);
		},
		onError: (err: Error) => {
			setInitSaveStatus({ type: 'error', message: err.message });
		},
	});

	// Build a map: userId → identity[] for quick lookup
	const userIdentityMap: Record<string, any[]> = {};
	for (const id of (teamIdentitiesData?.identities || [])) {
		if (!userIdentityMap[id.user_id]) userIdentityMap[id.user_id] = [];
		userIdentityMap[id.user_id].push(id);
	}

	// Derived data for access tab
	const allMembers: TeamMember[] = membersData?.members || [];
	const accessList: AccessEntry[] = accessData?.access || [];
	const privilegedMembers = allMembers.filter(m => m.role === 'owner' || m.role === 'admin');
	const accessUserIds = new Set(accessList.map(a => a.user_id));
	const privilegedUserIds = new Set(privilegedMembers.map(m => m.user_id));
	const addableMembers = allMembers.filter(
		m => m.role === 'member' && !accessUserIds.has(m.user_id)
	);

	// Platforms configured on this instance (from channel key assignments)
	const instancePlatforms = Object.keys(channelKeyAssignments);

	// Fetch instance, policy, and team default
	useEffect(() => {
		let cancelled = false;

		const fetchData = async () => {
			if (!firebaseUser || !instanceId) return;

			try {
				const headers = await getHeaders();

				// Fetch instance details
				const instanceResponse = await fetch(`${API_URL}/api/v1/instances/${instanceId}`, {
					headers: headers,
				});

				if (cancelled) return;

				let agentId: string | undefined;
				if (instanceResponse.ok) {
					const instanceData = await instanceResponse.json();
					setInstance(instanceData);
					agentId = instanceData?.agent_id;
				}

				// Fetch channel key assignments for this specific instance
				if (agentId) {
					try {
						const assignResponse = await fetch(`${API_URL}/api/v1/channel-keys/assignments`, { headers });
						if (assignResponse.ok) {
							const assignData = await assignResponse.json();
							const agentAssignments: Record<string, string> = {};
							for (const a of assignData.assignments || []) {
								if (a.agent_id === agentId && (!a.instance_id || a.instance_id === instanceId)) {
									agentAssignments[a.channel_type] = a.label;
								}
							}
							if (!cancelled) setChannelKeyAssignments(agentAssignments);
						}
					} catch (err) {
						console.error('Error fetching channel key assignments:', err);
					}
				}

				// Fetch instance policy
				const policyResponse = await fetch(`${API_URL}/api/v1/policies/${instanceId}`, {
					headers: headers,
				});

				if (cancelled) return;

				if (policyResponse.ok) {
					const policyData = await policyResponse.json();
					setPolicy(policyData);
					// Merge with default integrations for backward compatibility
					const configWithDefaults = {
						...policyData.config,
						// New instances using team default should have all sections off (inherit team config).
						// Legacy instances without section_overrides keep backward compat (all ON) via isSectionCustomized.
						section_overrides: policyData.config.section_overrides || (policyData.is_default
							? { security: false, ai_providers: false, integrations: false }
							: { security: true, ai_providers: true, integrations: true }),
						integrations: {
							...DEFAULT_INTEGRATIONS,
							...(policyData.config.integrations || {}),
							// Deep merge each integration
							telegram: { ...DEFAULT_INTEGRATIONS.telegram, ...(policyData.config.integrations?.telegram || {}) },
							slack: { ...DEFAULT_INTEGRATIONS.slack, ...(policyData.config.integrations?.slack || {}) },
							discord: { ...DEFAULT_INTEGRATIONS.discord, ...(policyData.config.integrations?.discord || {}) },
							webhook: { ...DEFAULT_INTEGRATIONS.webhook, ...(policyData.config.integrations?.webhook || {}) },
							local_port: { ...DEFAULT_INTEGRATIONS.local_port, ...(policyData.config.integrations?.local_port || {}) },
							email: { ...DEFAULT_INTEGRATIONS.email, ...(policyData.config.integrations?.email || {}) },
							wechat: { ...DEFAULT_INTEGRATIONS.wechat, ...(policyData.config.integrations?.wechat || {}) },
							whatsapp: { ...DEFAULT_INTEGRATIONS.whatsapp, ...(policyData.config.integrations?.whatsapp || {}) },
							imessage: { ...DEFAULT_INTEGRATIONS.imessage, ...(policyData.config.integrations?.imessage || {}) },
						},
						oauth: {
							...DEFAULT_OAUTH,
							...(policyData.config.oauth || {}),
							google: { ...DEFAULT_OAUTH.google, ...(policyData.config.oauth?.google || {}) },
							github: { ...DEFAULT_OAUTH.github, ...(policyData.config.oauth?.github || {}) },
							microsoft: { ...DEFAULT_OAUTH.microsoft, ...(policyData.config.oauth?.microsoft || {}) },
						},
						ai_providers: {
							...DEFAULT_AI_PROVIDERS,
							...(policyData.config.ai_providers || {}),
							anthropic: { ...DEFAULT_AI_PROVIDERS.anthropic, ...(policyData.config.ai_providers?.anthropic || {}) },
							openai: { ...DEFAULT_AI_PROVIDERS.openai, ...(policyData.config.ai_providers?.openai || {}) },
							google_ai: { ...DEFAULT_AI_PROVIDERS.google_ai, ...(policyData.config.ai_providers?.google_ai || {}) },
							openrouter: { ...DEFAULT_AI_PROVIDERS.openrouter, ...(policyData.config.ai_providers?.openrouter || {}) },
						},
						provider_order: policyData.config.provider_order || DEFAULT_PROVIDER_ORDER,
					};
					setConfig(configWithDefaults);
				}

				// Fetch team default policy for override indicators
				const teamDefaultResponse = await fetch(`${API_URL}/api/v1/policies/team-default`, {
					headers: headers,
				});

				if (cancelled) return;

				if (teamDefaultResponse.ok) {
					const teamDefaultData = await teamDefaultResponse.json();
					// Apply same defaults merging
					const teamDefaultWithDefaults = {
						...teamDefaultData.config,
						integrations: {
							...DEFAULT_INTEGRATIONS,
							...(teamDefaultData.config.integrations || {}),
							telegram: { ...DEFAULT_INTEGRATIONS.telegram, ...(teamDefaultData.config.integrations?.telegram || {}) },
							slack: { ...DEFAULT_INTEGRATIONS.slack, ...(teamDefaultData.config.integrations?.slack || {}) },
							discord: { ...DEFAULT_INTEGRATIONS.discord, ...(teamDefaultData.config.integrations?.discord || {}) },
							webhook: { ...DEFAULT_INTEGRATIONS.webhook, ...(teamDefaultData.config.integrations?.webhook || {}) },
							local_port: { ...DEFAULT_INTEGRATIONS.local_port, ...(teamDefaultData.config.integrations?.local_port || {}) },
							email: { ...DEFAULT_INTEGRATIONS.email, ...(teamDefaultData.config.integrations?.email || {}) },
							wechat: { ...DEFAULT_INTEGRATIONS.wechat, ...(teamDefaultData.config.integrations?.wechat || {}) },
							whatsapp: { ...DEFAULT_INTEGRATIONS.whatsapp, ...(teamDefaultData.config.integrations?.whatsapp || {}) },
							imessage: { ...DEFAULT_INTEGRATIONS.imessage, ...(teamDefaultData.config.integrations?.imessage || {}) },
						},
						oauth: {
							...DEFAULT_OAUTH,
							...(teamDefaultData.config.oauth || {}),
							google: { ...DEFAULT_OAUTH.google, ...(teamDefaultData.config.oauth?.google || {}) },
							github: { ...DEFAULT_OAUTH.github, ...(teamDefaultData.config.oauth?.github || {}) },
							microsoft: { ...DEFAULT_OAUTH.microsoft, ...(teamDefaultData.config.oauth?.microsoft || {}) },
						},
						ai_providers: {
							...DEFAULT_AI_PROVIDERS,
							...(teamDefaultData.config.ai_providers || {}),
							anthropic: { ...DEFAULT_AI_PROVIDERS.anthropic, ...(teamDefaultData.config.ai_providers?.anthropic || {}) },
							openai: { ...DEFAULT_AI_PROVIDERS.openai, ...(teamDefaultData.config.ai_providers?.openai || {}) },
							google_ai: { ...DEFAULT_AI_PROVIDERS.google_ai, ...(teamDefaultData.config.ai_providers?.google_ai || {}) },
							openrouter: { ...DEFAULT_AI_PROVIDERS.openrouter, ...(teamDefaultData.config.ai_providers?.openrouter || {}) },
						},
					};
					setTeamDefault(teamDefaultWithDefaults);
				}
			} catch (error) {
				if (!cancelled) console.error('Error fetching data:', error);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchData();
		return () => { cancelled = true; };
	}, [firebaseUser, instanceId]);

	const pollSyncStatus = async (commandId: string): Promise<void> => {
		const maxAttempts = 15; // 2s interval * 15 = 30s max
		for (let i = 0; i < maxAttempts; i++) {
			await new Promise(r => setTimeout(r, 2000));
			try {
				const pollHeaders = await getHeaders();
				const pollResp = await fetch(
					`${API_URL}/api/v1/policies/${instanceId}/sync-status/${commandId}`,
					{ headers: pollHeaders }
				);
				if (!pollResp.ok) break;
				const result = await pollResp.json();
				if (result.status === 'completed') {
					setSaveStatus({ type: 'success', message: 'Configuration synced to agent.' });
					setTimeout(() => setSaveStatus(null), 5000);
					return;
				}
				if (result.status === 'failed') {
					setSaveStatus({ type: 'error', message: `Agent failed to apply: ${result.error_message || 'Unknown error'}` });
					return;
				}
				if (result.status === 'expired') {
					setSaveStatus({ type: 'error', message: 'Sync expired — agent did not pick up the command.' });
					return;
				}
				// Still pending — continue polling
			} catch {
				break;
			}
		}
		setSaveStatus({ type: 'success', message: 'Sync pending — agent hasn\'t confirmed yet.' });
		setTimeout(() => setSaveStatus(null), 5000);
	};

	const handleSave = async () => {
		if (!firebaseUser || !instanceId || !config) return;

		setSaving(true);
		setSaveStatus(null);
		try {
			const headers = await getHeaders();
			// Strip is_configured from ai_providers before saving — it's derived from
			// credential status (separate table) and shouldn't be baked into policy data
			const configToSave = { ...config };
			if (configToSave.ai_providers) {
				const cleanedProviders = { ...configToSave.ai_providers };
				for (const key of Object.keys(cleanedProviders) as AIProviderName[]) {
					const { is_configured: _, ...rest } = cleanedProviders[key];
					cleanedProviders[key] = rest as typeof cleanedProviders[typeof key];
				}
				configToSave.ai_providers = cleanedProviders;
			}
			const response = await fetch(`${API_URL}/api/v1/policies/${instanceId}`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({ config: configToSave }),
			});

			if (response.ok) {
				const data = await response.json();
				setPolicy(prev => prev ? { ...prev, ...data, is_default: false } : null);

				// Check for pool warnings (channel key pool exhausted)
				if (data.pool_warnings && data.pool_warnings.length > 0) {
					setSaveStatus({ type: 'error', message: data.pool_warnings.join(' ') });
				} else {
					setSaveStatus({ type: 'success', message: 'Configuration saved. Syncing to agent...' });
				}

				// Re-fetch channel key assignments (may have changed due to auto-assign/release)
				try {
					const assignHeaders = await getHeaders();
					const assignResponse = await fetch(`${API_URL}/api/v1/channel-keys/assignments`, { headers: assignHeaders });
					if (assignResponse.ok) {
						const assignData = await assignResponse.json();
						const agentAssignments: Record<string, string> = {};
						for (const a of assignData.assignments || []) {
							if (a.agent_id === instance?.agent_id && (!a.instance_id || a.instance_id === instanceId)) {
								agentAssignments[a.channel_type] = a.label;
							}
						}
						setChannelKeyAssignments(agentAssignments);
					}
				} catch { /* ignore assignment refresh errors */ }

				// Poll for sync status
				if (data.command_id && !(data.pool_warnings?.length > 0)) {
					pollSyncStatus(data.command_id);
				} else if (!(data.pool_warnings?.length > 0)) {
					setTimeout(() => setSaveStatus(null), 5000);
				}
			} else {
				const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
				setSaveStatus({ type: 'error', message: errorData.error || 'Failed to save configuration' });
			}
		} catch (error) {
			console.error('Error saving policy:', error);
			setSaveStatus({ type: 'error', message: 'Network error - please try again' });
		} finally {
			setSaving(false);
		}
	};

	const updateConfig = (updates: Partial<PolicyConfig>) => {
		setConfig(prev => prev ? { ...prev, ...updates } : null);
	};

	// Handler for connecting AI providers (opens credential modals)
	const handleConnectProvider = (provider: AIProviderName) => {
		if (provider === 'anthropic') {
			setShowClaudeTokenModal(true);
			setClaudeTokenInput('');
		} else {
			setShowApiKeyModal(provider);
			setApiKeyInput('');
		}
	};

	// Handler for removing AI provider credentials
	const handleRemoveProvider = async (provider: AIProviderName) => {
		if (!firebaseUser || !instanceId || !config) return;

		try {
			const headers = await getHeaders();

			// Call remove credential API
			const response = await fetch(`${API_URL}/api/v1/credentials/${instanceId}/${provider}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				// Update local state to reflect removed status
				if (config.ai_providers) {
					setConfig({
						...config,
						ai_providers: {
							...config.ai_providers,
							[provider]: {
								...config.ai_providers[provider],
								enabled: false,
								is_configured: false,
								allowed_models: [],
							},
						},
					});
				}
				setSaveStatus({ type: 'success', message: `${provider} credential removed. Click "Save Configuration" to sync.` });
				setTimeout(() => setSaveStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setSaveStatus({ type: 'error', message: errorData.error || 'Failed to remove credential' });
			}
		} catch (error) {
			console.error('Error removing credential:', error);
			setSaveStatus({ type: 'error', message: 'Network error - please try again' });
		}
	};

	// Save Claude token
	const handleSaveClaudeToken = async () => {
		if (!firebaseUser || !instanceId || !claudeTokenInput.trim()) return;

		setSavingCredential(true);
		setSaveStatus(null);

		try {
			const headers = await getHeaders();

			// Save token via credentials API
			const response = await fetch(`${API_URL}/api/v1/credentials/${instanceId}`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({
					provider: 'anthropic',
					api_key: claudeTokenInput.trim(),
				}),
			});

			if (response.ok) {
				// Update local state to reflect configured status
				if (config?.ai_providers) {
					const existingProvider = config.ai_providers.anthropic;
					const defaultModels = !existingProvider.is_configured
						? PROVIDER_MODELS.anthropic.map(m => m.id)
						: existingProvider.allowed_models;
					setConfig({
						...config,
						ai_providers: {
							...config.ai_providers,
							anthropic: {
								...existingProvider,
								is_configured: true,
								enabled: existingProvider.is_configured ? existingProvider.enabled : true,
								allowed_models: defaultModels,
							},
						},
					});
				}
				setShowClaudeTokenModal(false);
				setClaudeTokenInput('');
				setSaveStatus({ type: 'success', message: 'Claude token configured. Select models and click "Save Configuration" to sync.' });
				setTimeout(() => setSaveStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setSaveStatus({ type: 'error', message: errorData.error || 'Failed to save token' });
			}
		} catch (error) {
			console.error('Error saving credential:', error);
			setSaveStatus({ type: 'error', message: 'Network error - please try again' });
		} finally {
			setSavingCredential(false);
		}
	};

	// Fetch gateway config from agent
	const fetchGatewayConfig = async () => {
		if (!firebaseUser || !instanceId) return;

		setLoadingGatewayConfig(true);
		setGatewayConfigStatus(null);
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}/gateway-config`, {
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				setGatewayConfig(data.config);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setGatewayConfigStatus({ type: 'error', message: errorData.error || 'Failed to fetch gateway config' });
			}
		} catch (error) {
			console.error('Error fetching gateway config:', error);
			setGatewayConfigStatus({ type: 'error', message: 'Network error' });
		} finally {
			setLoadingGatewayConfig(false);
		}
	};

	// Save gateway config to agent
	const saveGatewayConfig = async (configStr: string) => {
		if (!firebaseUser || !instanceId) return;

		setSavingGatewayConfig(true);
		setGatewayConfigStatus(null);
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/instances/${instanceId}/gateway-config`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({ config: configStr }),
			});

			if (response.ok) {
				setGatewayConfigStatus({ type: 'success', message: 'Gateway config saved successfully.' });
				setGatewayConfig(configStr);
				setTimeout(() => setGatewayConfigStatus(null), 5000);
			} else {
				const errorData = await response.json().catch(() => ({}));
				setGatewayConfigStatus({ type: 'error', message: errorData.error || 'Failed to save gateway config' });
			}
		} catch (error) {
			console.error('Error saving gateway config:', error);
			setGatewayConfigStatus({ type: 'error', message: 'Network error' });
		} finally {
			setSavingGatewayConfig(false);
		}
	};

	if (loading) {
		return (
			<Layout title="Workspace Settings" subtitle="Loading...">
				<div className="flex items-center justify-center h-64">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
				</div>
			</Layout>
		);
	}

	if (!instance || !config) {
		return (
			<Layout title="Workspace Not Found" subtitle="The requested workspace could not be found">
				<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-12 text-center">
					<p className="text-slate-600 dark:text-slate-400 mb-4">This workspace doesn't exist or you don't have access to it.</p>
					<Link to="/machines" className="text-primary-600 hover:text-primary-700 font-medium">
						Back to Machines
					</Link>
				</div>
			</Layout>
		);
	}

	return (
		<Layout
			title="Configuration"
			subtitle={`Clawdbot v${instance.clawdbot_version} on ${instance.agent_hostname || 'Unknown Host'}`}
		>
			{/* Breadcrumb */}
			<div className="mb-6">
				<Link to={`/machines/${instance.agent_id}`} className="text-sm text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center">
					<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
					</svg>
					Back to Device
				</Link>
			</div>

			{/* Status Banner - Using Team Default */}
			{policy?.is_default && (
				<div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 flex items-center justify-between">
					<div className="flex items-center space-x-3">
						<svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
						</svg>
						<span className="text-sm text-blue-800 dark:text-blue-200">Using team defaults. Customize settings below to create a workspace-specific configuration.</span>
					</div>
				</div>
			)}

			{/* Status Banner - Save/Error Messages */}
			{saveStatus && (
				<div className={`rounded-xl p-4 mb-6 flex items-center justify-between ${
					saveStatus.type === 'success'
						? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
						: 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
				}`}>
					<div className="flex items-center space-x-3">
						{saveStatus.type === 'success' ? (
							<svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
							</svg>
						) : (
							<svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
							</svg>
						)}
						<span className={`text-sm ${
							saveStatus.type === 'success'
								? 'text-green-800 dark:text-green-200'
								: 'text-red-800 dark:text-red-200'
						}`}>
							{saveStatus.message}
						</span>
					</div>
					<button
						onClick={() => setSaveStatus(null)}
						className={`p-1 rounded-lg transition-colors cursor-pointer ${
							saveStatus.type === 'success'
								? 'hover:bg-green-100 dark:hover:bg-green-900/40 text-green-600 dark:text-green-400'
								: 'hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400'
						}`}
					>
						<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>
			)}

			<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
				<PolicyConfigTabs
					config={config}
					onConfigChange={updateConfig}
					teamDefault={teamDefault || undefined}
					showOverrideToggles={true}
					onConnectProvider={handleConnectProvider}
					onRemoveProvider={handleRemoveProvider}
					gatewayConfig={gatewayConfig}
					onFetchGatewayConfig={fetchGatewayConfig}
					onSaveGatewayConfig={saveGatewayConfig}
					loadingGatewayConfig={loadingGatewayConfig}
					savingGatewayConfig={savingGatewayConfig}
					gatewayConfigStatus={gatewayConfigStatus}
					channelKeyAssignments={channelKeyAssignments}
					skillsContent={
						<div>
							<div className="mb-4">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Skills</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400">
									Add capabilities to this workspace — skills extend what your bot can do
								</p>
							</div>
							<SkillManager instanceId={instanceId} />
						</div>
					}
					initContent={isOwnerOrAdmin ? (
						<div className="p-6 space-y-6">
							{/* Status banner */}
							{initSaveStatus && (
								<div className={`px-4 py-3 rounded-lg text-sm ${
									initSaveStatus.type === 'success'
										? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
										: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
								}`}>
									{initSaveStatus.message}
								</div>
							)}

							{/* Environment Variables */}
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Environment Variables</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
									Set environment variables for the workspace shell session
								</p>
								{envVars.length === 0 ? (
									<p className="text-sm text-slate-400 dark:text-slate-500 italic mb-2">No environment variables configured</p>
								) : (
									<div className="space-y-2 mb-2">
										{envVars.map((v, i) => (
											<div key={i} className="flex items-center gap-2">
												<input
													type="text"
													value={v.key}
													onChange={(e) => {
														const updated = [...envVars];
														updated[i] = { ...updated[i], key: e.target.value };
														setEnvVars(updated);
													}}
													placeholder="KEY"
													className="w-1/3 px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
												/>
												<span className="text-slate-400">=</span>
												<input
													type="text"
													value={v.value}
													onChange={(e) => {
														const updated = [...envVars];
														updated[i] = { ...updated[i], value: e.target.value };
														setEnvVars(updated);
													}}
													placeholder="value"
													className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
												/>
												<button
													onClick={() => setEnvVars(envVars.filter((_, j) => j !== i))}
													className="p-2 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
													title="Remove"
												>
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
													</svg>
												</button>
											</div>
										))}
									</div>
								)}
								<button
									onClick={() => setEnvVars([...envVars, { key: '', value: '' }])}
									className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium cursor-pointer"
								>
									+ Add Variable
								</button>
							</div>

							{/* Init Script */}
							<div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Init Script</h3>
								<p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
									Runs at the start of each shell session
								</p>
								<textarea
									value={initScript}
									onChange={(e) => setInitScript(e.target.value)}
									placeholder={"#!/bin/bash\n# Commands to run at shell startup..."}
									rows={10}
									className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white resize-y"
								/>
							</div>

							{/* Save Button */}
							<button
								onClick={() => saveInitMutation.mutate()}
								disabled={saveInitMutation.isPending}
								className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 cursor-pointer"
							>
								{saveInitMutation.isPending ? 'Saving...' : 'Save Init Config'}
							</button>

							{/* Init Log */}
							{initData?.last_run_at && (
								<div className="border-t border-slate-200 dark:border-slate-700 pt-6">
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">Init Log</h3>
									<div className="flex items-center gap-3 mb-3">
										<span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
											initData.last_run_exit_code === 0
												? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
												: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
										}`}>
											{initData.last_run_exit_code === 0 ? 'Success' : `Failed (exit code ${initData.last_run_exit_code})`}
										</span>
										<span className="text-xs text-slate-500 dark:text-slate-400">
											{new Date(initData.last_run_at).toLocaleString()}
										</span>
									</div>
									{initData.last_run_output ? (
										<pre className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-mono text-slate-700 dark:text-slate-300 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap">
											{initData.last_run_output}
										</pre>
									) : (
										<p className="text-sm text-slate-400 dark:text-slate-500 italic">No output</p>
									)}
								</div>
							)}
						</div>
					) : undefined}
				accessContent={isOwnerOrAdmin ? (
						<div className="space-y-6">
							{/* Full Access (Role-Based) */}
							<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
								<div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Full Access (Role-Based)</h3>
									<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
										Owners and admins automatically have full access to all instances.
									</p>
								</div>
								<div className="p-6">
									{membersLoading ? (
										<div className="flex justify-center py-8">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
										</div>
									) : privilegedMembers.length > 0 ? (
										<div className="space-y-3">
											{privilegedMembers.map(member => {
												const identities = userIdentityMap[member.user_id] || [];
												return (
												<div
													key={member.user_id}
													className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
												>
													<div className="flex items-center space-x-3">
														<div className="w-9 h-9 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm">
															{(member.display_name || member.email).charAt(0).toUpperCase()}
														</div>
														<div>
															<div className="flex items-center space-x-2">
																<p className="font-medium text-slate-900 dark:text-white text-sm">
																	{member.display_name || member.email}
																</p>
																<span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
																	member.role === 'owner'
																		? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
																		: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
																}`}>
																	{member.role === 'owner' ? 'Owner' : 'Admin'}
																</span>
															</div>
															{member.display_name && (
																<p className="text-xs text-slate-500 dark:text-slate-400">{member.email}</p>
															)}
															{instancePlatforms.length > 0 && (
																<div className="flex items-center gap-1 mt-1">
																	{instancePlatforms.map(p => {
																		const linked = identities.find((id: any) => id.platform === p);
																		return (
																			<span
																				key={p}
																				title={linked ? `${platformLabel(p)}: ${linked.verified ? 'Verified' : 'Pending verification'}` : `${platformLabel(p)}: Not linked`}
																				className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
																					linked
																						? linked.verified
																							? 'bg-green-500'
																							: 'bg-yellow-500'
																						: 'bg-slate-300 dark:bg-slate-600'
																				}`}
																			>
																				{platformIcon(p)}
																			</span>
																		);
																	})}
																</div>
															)}
														</div>
													</div>
												</div>
												);
											})}
										</div>
									) : (
										<p className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">No owners or admins found.</p>
									)}
								</div>
							</div>

							{/* Instance Access (explicit grants for members) */}
							<div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
								<div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
									<h3 className="text-lg font-semibold text-slate-900 dark:text-white">Instance Access</h3>
									<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
										Members who have been explicitly granted access to this workspace.
									</p>
								</div>
								<div className="p-6">
									{accessLoading ? (
										<div className="flex justify-center py-8">
											<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
										</div>
									) : accessList.length > 0 ? (
										<div className="space-y-3 mb-6">
											{accessList.filter(a => !privilegedUserIds.has(a.user_id)).map(entry => {
												const identities = userIdentityMap[entry.user_id] || [];
												return (
												<div
													key={entry.user_id}
													className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
												>
													<div className="flex items-center space-x-3">
														<div className="w-9 h-9 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center text-white text-sm font-semibold shadow-sm">
															{(entry.display_name || entry.email).charAt(0).toUpperCase()}
														</div>
														<div>
															<p className="font-medium text-slate-900 dark:text-white text-sm">
																{entry.display_name || entry.email}
															</p>
															{entry.display_name && (
																<p className="text-xs text-slate-500 dark:text-slate-400">{entry.email}</p>
															)}
															<p className="text-xs text-slate-400 dark:text-slate-500">
																Granted {new Date(entry.granted_at).toLocaleDateString()}
															</p>
															{instancePlatforms.length > 0 && (
																<div className="flex items-center gap-1 mt-1">
																	{instancePlatforms.map(p => {
																		const linked = identities.find((id: any) => id.platform === p);
																		return (
																			<span
																				key={p}
																				title={linked ? `${platformLabel(p)}: ${linked.verified ? 'Verified' : 'Pending verification'}` : `${platformLabel(p)}: Not linked`}
																				className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
																					linked
																						? linked.verified
																							? 'bg-green-500'
																							: 'bg-yellow-500'
																						: 'bg-slate-300 dark:bg-slate-600'
																				}`}
																			>
																				{platformIcon(p)}
																			</span>
																		);
																	})}
																</div>
															)}
														</div>
													</div>
													<button
														onClick={() => {
															if (confirm(`Remove access for ${entry.display_name || entry.email}?`)) {
																revokeMutation.mutate(entry.user_id);
															}
														}}
														disabled={revokeMutation.isPending}
														className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
													>
														Remove
													</button>
												</div>
												);
											})}
										</div>
									) : (
										<div className="text-center py-6 mb-6">
											<svg className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
											</svg>
											<p className="text-sm text-slate-500 dark:text-slate-400">No members have been granted explicit access yet.</p>
											<p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Use the control below to add team members.</p>
										</div>
									)}

									{/* Add Member Control */}
									{(grantMutation.isError || revokeMutation.isError) && (
										<div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
											<p className="text-sm text-red-700 dark:text-red-300">
												{(grantMutation.error as Error)?.message || (revokeMutation.error as Error)?.message || 'An error occurred'}
											</p>
										</div>
									)}

									<div className="border-t border-slate-200 dark:border-slate-700 pt-4">
										<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
											Add Member
										</label>
										<div className="flex items-center space-x-3">
											<select
												value={selectedMemberToAdd}
												onChange={(e) => setSelectedMemberToAdd(e.target.value)}
												className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
											>
												<option value="">Select a team member...</option>
												{addableMembers.map(member => {
													const identities = userIdentityMap[member.user_id] || [];
													const linkedPlatforms = identities.map((id: any) => platformLabel(id.platform));
													const suffix = linkedPlatforms.length > 0 ? ` [${linkedPlatforms.join(', ')}]` : ' [no linked platforms]';
													return (
													<option key={member.user_id} value={member.user_id}>
														{member.display_name ? `${member.display_name} (${member.email})` : member.email}{suffix}
													</option>
													);
												})}
											</select>
											<button
												onClick={() => {
													if (selectedMemberToAdd) {
														grantMutation.mutate(selectedMemberToAdd);
													}
												}}
												disabled={!selectedMemberToAdd || grantMutation.isPending}
												className="px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-lg font-medium text-sm transition-all cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
											>
												{grantMutation.isPending ? (
													<>
														<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
														<span>Adding...</span>
													</>
												) : (
													<span>Add</span>
												)}
											</button>
										</div>
										{addableMembers.length === 0 && !membersLoading && (
											<p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
												All team members already have access (either role-based or explicitly granted).
											</p>
										)}
									</div>
								</div>
							</div>

							{/* Platform Identity Legend */}
							{instancePlatforms.length > 0 && (
								<div className="flex items-center gap-4 px-1 text-xs text-slate-400 dark:text-slate-500">
									<span className="font-medium text-slate-500 dark:text-slate-400">Platform identity:</span>
									<span className="flex items-center gap-1">
										<span className="w-3 h-3 rounded-full bg-green-500 inline-block" /> Linked & verified
									</span>
									<span className="flex items-center gap-1">
										<span className="w-3 h-3 rounded-full bg-yellow-500 inline-block" /> Pending verification
									</span>
									<span className="flex items-center gap-1">
										<span className="w-3 h-3 rounded-full bg-slate-300 dark:bg-slate-600 inline-block" /> Not linked
									</span>
								</div>
							)}
						</div>
					) : undefined}
				/>

				{/* Save Bar */}
				<div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
					<div className="text-sm text-slate-500 dark:text-slate-400">
						{policy?.updated_at ? (
							<>Last saved: {new Date(policy.updated_at).toLocaleString()}</>
						) : (
							<>Never saved</>
						)}
					</div>
					<div className="flex items-center space-x-3">
						<Link
							to={`/machines/${instance.agent_id}`}
							className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl font-medium transition-colors"
						>
							Cancel
						</Link>
						<button
							onClick={handleSave}
							disabled={saving}
							className="px-6 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl disabled:shadow-none transition-all duration-200 cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
						>
							{saving ? (
								<>
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
									<span>Saving...</span>
								</>
							) : (
								<span>Save Configuration</span>
							)}
						</button>
					</div>
				</div>
			</div>

			{/* API Key Modal (OpenAI, Google AI, OpenRouter) */}
			{showApiKeyModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
						<div className="p-6 border-b border-slate-200 dark:border-slate-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
									{showApiKeyModal === 'openai' && 'Configure OpenAI API Key'}
									{showApiKeyModal === 'google_ai' && 'Configure Google AI API Key'}
									{showApiKeyModal === 'openrouter' && 'Configure OpenRouter API Key'}
								</h3>
								<button
									onClick={() => setShowApiKeyModal(null)}
									className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-4">
							<div>
								<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
									API Key
								</label>
								<input
									type="password"
									value={apiKeyInput}
									onChange={(e) => setApiKeyInput(e.target.value)}
									placeholder={
										showApiKeyModal === 'openai' ? 'sk-...' :
										showApiKeyModal === 'google_ai' ? 'AIza...' :
										'Enter your API key'
									}
									className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400"
								/>
							</div>

							<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
								<div className="flex items-start space-x-2">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<p className="text-sm text-amber-700 dark:text-amber-300">
										Your API key will be encrypted and securely stored.
									</p>
								</div>
							</div>
						</div>

						<div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
							<button
								onClick={() => setShowApiKeyModal(null)}
								className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={async () => {
									if (!apiKeyInput.trim()) return;
									if (!firebaseUser || !instanceId || !showApiKeyModal) return;

									setSavingCredential(true);
									setSaveStatus(null);

									try {
										const headers = await getHeaders();
										const providerKey = showApiKeyModal;

										const response = await fetch(`${API_URL}/api/v1/credentials/${instanceId}`, {
											method: 'PUT',
											headers: headers,
											body: JSON.stringify({
												provider: providerKey,
												api_key: apiKeyInput.trim(),
											}),
										});

										if (response.ok) {
											if (config?.ai_providers) {
												const existingProvider = config.ai_providers[providerKey];
												const defaultModels = !existingProvider.is_configured
													? PROVIDER_MODELS[providerKey].map(m => m.id)
													: existingProvider.allowed_models;
												setConfig({
													...config,
													ai_providers: {
														...config.ai_providers,
														[providerKey]: {
															...existingProvider,
															is_configured: true,
															enabled: existingProvider.is_configured ? existingProvider.enabled : true,
															allowed_models: defaultModels,
														},
													},
												});
											}
											setShowApiKeyModal(null);
											setApiKeyInput('');
											setSaveStatus({ type: 'success', message: 'API key configured. Select models and click "Save Configuration" to sync.' });
											setTimeout(() => setSaveStatus(null), 5000);
										} else {
											const errorData = await response.json().catch(() => ({}));
											setSaveStatus({ type: 'error', message: errorData.error || 'Failed to save API key' });
										}
									} catch (error) {
										console.error('Error saving credential:', error);
										setSaveStatus({ type: 'error', message: 'Network error - please try again' });
									} finally {
										setSavingCredential(false);
									}
								}}
								disabled={!apiKeyInput.trim() || savingCredential}
								className="px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
							>
								{savingCredential ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Saving...</span>
									</>
								) : (
									<span>Save Key</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Claude Token Modal */}
			{showClaudeTokenModal && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
					<div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
						<div className="p-6 border-b border-slate-200 dark:border-slate-700">
							<div className="flex items-center justify-between">
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
									Connect Claude
								</h3>
								<button
									onClick={() => setShowClaudeTokenModal(false)}
									className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors cursor-pointer"
								>
									<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
									</svg>
								</button>
							</div>
						</div>

						<div className="p-6 space-y-4">
							{/* Instructions */}
							<div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
								<p className="text-sm font-medium text-slate-900 dark:text-white mb-3">
									Step 1: Run the setup command
								</p>
								<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
									Open a terminal on your local machine and run:
								</p>
								<div className="bg-slate-900 dark:bg-black rounded-lg p-3 font-mono text-sm text-green-400 flex items-center justify-between">
									<code>claude setup-token</code>
									<button
										onClick={() => navigator.clipboard.writeText('claude setup-token')}
										className="p-1.5 text-slate-400 hover:text-white transition-colors cursor-pointer"
										title="Copy command"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
										</svg>
									</button>
								</div>
								<p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
									Complete the OAuth flow in your browser. The token will be displayed in your terminal.
								</p>
							</div>

							<div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4">
								<p className="text-sm font-medium text-slate-900 dark:text-white mb-3">
									Step 2: Paste the token
								</p>
								<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
									Copy the token from your terminal and paste it below:
								</p>
								<input
									type="password"
									value={claudeTokenInput}
									onChange={(e) => setClaudeTokenInput(e.target.value)}
									placeholder="sk-ant-oat01-..."
									className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 font-mono text-sm"
								/>
							</div>

							<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
								<div className="flex items-start space-x-2">
									<svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
									</svg>
									<p className="text-sm text-amber-700 dark:text-amber-300">
										Your token will be encrypted and securely stored. It will be synced to your Clawdbot workspace automatically.
									</p>
								</div>
							</div>
						</div>

						<div className="p-6 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 flex justify-end space-x-3">
							<button
								onClick={() => setShowClaudeTokenModal(false)}
								className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors cursor-pointer"
							>
								Cancel
							</button>
							<button
								onClick={handleSaveClaudeToken}
								disabled={!claudeTokenInput.trim() || savingCredential}
								className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-slate-400 disabled:to-slate-400 text-white rounded-lg font-medium transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center space-x-2"
							>
								{savingCredential ? (
									<>
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
										<span>Saving...</span>
									</>
								) : (
									<span>Save Token</span>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</Layout>
	);
}
